import { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { MenuItem, Select, Pagination, Stack } from '@mui/material';
import {
	Sparkles,
	TrendingUp,
	Clock,
	PhoneCall,
	PauseCircle,
	CheckCircle,
	XCircle,
	Ban,
	UserPlus,
	Users,
	ChevronRight,
} from 'lucide-react';
import LeadsView from '@ComponentViews/LeadsView';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import UserAvatar from '@CommonRepresentationalDisplay/UserAvatar';
import FullUserAvatar from '@CommonRepresentationalDisplay/FullUserAvatar';
import LeadsService from '@Helpers/URLHelpers/LeadsService';
import ErrorHandler from '@Root/ErrorHandler';
import './AssignedLeadsList.scss';

const LEAD_STATUSES = [
	{ key: 'newLeads', label: 'New Leads' },
	{ key: 'contacted', label: 'Contacted' },
	{ key: 'followUp', label: 'Follow Up' },
	{ key: 'active', label: 'Active' },
	{ key: 'onHold', label: 'On Hold' },
	{ key: 'onboarded', label: 'Onboarded' },
	{ key: 'closed', label: 'Closed' },
	{ key: 'lost', label: 'Lost' },
];

const getStatusBadge = (status) => {
	switch (status) {
		case 'newLeads':
			return {
				className: 'status-new',
				icon: <Sparkles size={11} />,
				label: 'New',
			};
		case 'contacted':
			return {
				className: 'status-hot',
				icon: <TrendingUp size={11} />,
				label: 'Contacted',
			};
		case 'active':
			return {
				className: 'status-warm',
				icon: <Clock size={11} />,
				label: 'Active',
			};
		case 'followUp':
			return {
				className: 'status-info',
				icon: <PhoneCall size={11} />,
				label: 'Follow Up',
			};
		case 'onHold':
			return {
				className: 'status-muted',
				icon: <PauseCircle size={11} />,
				label: 'On Hold',
			};
		case 'onboarded':
			return {
				className: 'status-success',
				icon: <CheckCircle size={11} />,
				label: 'Onboarded',
			};
		case 'closed':
			return {
				className: 'status-danger',
				icon: <XCircle size={11} />,
				label: 'Closed',
			};
		case 'lost':
			return {
				className: 'status-danger-soft',
				icon: <Ban size={11} />,
				label: 'Lost',
			};
		default:
			return {
				className: 'status-default',
				icon: null,
				label: status,
			};
	}
};

export default function AssignedLeadsList() {

	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [cursor, setCursor] = useState('auto');
	const [leadsList, setLeadsList] = useState([]);
	const [selectedLead, setSelectedLead] = useState(null);
	const [page, setPage] = useState(1);
	const [size, setSize] = useState(5);
	const [totalPages, setTotalPages] = useState(1);
	const [totalResult, setTotalResult] = useState(0);
	const [openLeadView, setOpenLeadView] = useState(false);
	const [expandedRows, setExpandedRows] = useState(null);
	const leadsListRef = useRef(null);
	const { activeUserId } = useReduxActiveUser();
	const { currentProjectId } = useReduxProject();
	const leadsService = new LeadsService();
	const errorHandler = new ErrorHandler();
	const [defaultFilter, setDefaultFilter] = useState({
		leadsFilter: {
			projectIdList: [currentProjectId],
			statusList: [],
			assigneeIdList: [activeUserId],
			sortBy: 'createTimeStamp',
			sortAsc: false,
		},
		page: 0,
		size: 5,
	});

	useEffect(() => {
		if (currentProjectId && activeUserId) {
			setDefaultFilter((prev) => ({
				...prev,
				leadsFilter: {
					...prev.leadsFilter,
					projectIdList: [currentProjectId],
					assigneeIdList: [activeUserId],
				},
			}));
			fetchLeadsData(0, size);
		}
	}, [currentProjectId, activeUserId]);

	const safeJsonParse = (value, fallback = null) => {
		try {
			if (typeof value === 'string') {
				return JSON.parse(value);
			}
			return value;
		} catch (e) {
			return fallback;
		}
	};

	const parseLeadsData = (item) => ({
		...item,
		details: safeJsonParse(item.details, {}),
		history: safeJsonParse(item.history, []),
		comments: safeJsonParse(item.comments, []),
	});

	const fetchLeadsData = async (apiPage, pageSize = size) => {
		setLoading(true);
		try {
			const filter = {
				...defaultFilter,
				page: apiPage < 0 ? 0 : apiPage,
				size: pageSize,
			};
			let response = await leadsService.getFilteredList(filter);
			if (response.status < 400) {
				response = JSON.parse(response.body);
				const parsedLeads = response.leadsList.map(parseLeadsData);
				setLeadsList(parsedLeads);
				setTotalResult(response.totalItems || 0);
				setTotalPages(response.totalPages || 1);
			}
		} catch (err) {
			errorHandler.handleError(err);
		} finally {
			setLoading(false);
		}
	};

	const handleStatusChange = async (value, id) => {
		try {
			setCursor('wait');
			const bodyObj = {
				id,
				status: value,
			};
			let updateStatus = await leadsService.updateStatus(bodyObj);
			if (updateStatus.status < 400) {
				updateStatus = JSON.parse(updateStatus.body);
				fetchLeadsData(page - 1, size);
			}
		} catch (err) {
			console.error(err);
		} finally {
			setCursor('auto');
		}
	};

	const handleOpenLeadView = (lead) => {
		setSelectedLead(lead);
		setOpenLeadView(true);
	};

	const handleCloseLeadView = () => {
		setSelectedLead(null);
		setOpenLeadView(false);
	};

	const handleNewLeads = () => {
		navigate('/home/leads');
	};

	const handlePaginationClick = (event, value) => {
		setPage(value);
		fetchLeadsData(value - 1, size);
	};

	const handleSizeChange = (event) => {
		const newSize = parseInt(event.target.value, 10);
		setSize(newSize);
		setPage(1);
		fetchLeadsData(0, newSize);
	};

	const getItemName = (row) => (
		<div className='assigned-lead' onClick={() => handleOpenLeadView(row)}>
			<div className='assigned-lead__avatar'>
				<UserAvatar name={row?.name} size='small' />
			</div>
			<div className='assigned-lead__content'>
				<p className='assigned-lead__name'>{row?.name || 'Unnamed Lead'}</p>
				<p className='assigned-lead__company'>
					{row?.companyName || row?.contactNumber || 'No contact information'}
				</p>
			</div>
		</div>
	);

	const getItemAssignee = (row) => (
		<div className='assigned-lead__assignee'>
			<FullUserAvatar profileId={row?.assigneeUserId} size='small' />
		</div>
	);

	const getCreateDate = (row) => (
		<span className='assigned-lead__time'>
			{row?.createTimeStamp
				? moment(new Date(row.createTimeStamp)).fromNow()
				: '-'}
		</span>
	);

	const getItemStatus = (row) => {
		const status = getStatusBadge(row?.status);
		return (
			<Select
				className='assigned-lead-status-select'
				variant='standard'
				disableUnderline
				IconComponent={() => null}
				value={row?.status}
				onChange={(e) => handleStatusChange(e.target.value, row?.id)}
				renderValue={(selected) => {
					const selectedStatus = getStatusBadge(selected);
					return (
						<span className={`lead-status ${selectedStatus.className}`}>
							{selectedStatus.icon}
							{selectedStatus.label}
						</span>
					);
				}}
				sx={{
					'& .MuiSelect-select': {
						padding: '0 !important',
						display: 'flex',
						alignItems: 'center',
					},
				}}
			>
				{LEAD_STATUSES.map((item) => {
					const itemStatus = getStatusBadge(item.key);
					return (
						<MenuItem
							value={item.key}
							key={item.key}
							sx={{
								padding: '6px 9px',
							}}
						>
							<span className={`lead-status ${itemStatus.className}`}>
								{itemStatus.icon}
								{itemStatus.label}
							</span>
						</MenuItem>
					);
				})}
			</Select>
		);
	};

	const emptyList = () => (
		<div className='empty-leads'>
			<div className='empty-leads__icon'>
				<Users size={22} />
			</div>
			<h4>No leads yet</h4>
			<p>Assigned leads will appear here.</p>
			<button onClick={handleNewLeads} className='empty-leads__button'>
				<UserPlus size={13} />
				Add Lead
			</button>
		</div>
	);

	const footer = (
		<div className='assigned-leads-footer'>
			<div className='assigned-leads-footer__left'>
				<span>{totalResult} leads</span>
				<span className='assigned-leads-footer__separator'>•</span>
				<span>
					Page {page} of {totalPages}
				</span>
			</div>
			<div className='assigned-leads-footer__right'>
				<Stack spacing={0}>
					<Pagination
						count={totalPages}
						page={page}
						onChange={handlePaginationClick}
						size='small'
						siblingCount={0}
						boundaryCount={1}
						showFirstButton={false}
						showLastButton={false}
					/>
				</Stack>
				<button className='assigned-leads-add' onClick={handleNewLeads}>
					<UserPlus size={13} />
					Add lead
				</button>
			</div>
		</div>
	);

	return (
		<>
			<div className='assigned-leads' style={{ cursor }}>
				<div className='assigned-leads__header'>
					<div className='assigned-leads__heading'>
						<div className='assigned-leads__icon'>
							<Users size={15} />
						</div>
						<div>
							<div className='assigned-leads__title'>Assigned leads</div>
							<div className='assigned-leads__subtitle'>Your latest leads</div>
						</div>
					</div>
					<button className='assigned-leads__view-all' onClick={handleNewLeads}>
						View all
						<ChevronRight size={13} />
					</button>
				</div>
				<div className='assigned-leads__table'>
					<DataTable
						value={leadsList}
						ref={leadsListRef}
						loading={loading}
						scrollable
						scrollHeight='245px'
						footer={footer}
						emptyMessage={emptyList}
						dataKey='id'
						expandedRows={expandedRows}
						onRowToggle={(e) => setExpandedRows(e.data)}
					>
						<Column
							field='name'
							header='Lead'
							body={getItemName}
							style={{
								minWidth: '210px',
								width: '42%',
							}}
						/>
						<Column
							field='assignee'
							header='Owner'
							body={getItemAssignee}
							style={{
								width: '17%',
							}}
						/>
						<Column
							field='status'
							header='Status'
							body={getItemStatus}
							style={{
								width: '23%',
							}}
						/>
						<Column
							field='createDate'
							header='Added'
							body={getCreateDate}
							style={{
								width: '18%',
							}}
						/>
					</DataTable>
				</div>
			</div>
			{openLeadView && (
				<LeadsView
					open={openLeadView}
					onClose={handleCloseLeadView}
					id={selectedLead?.id}
				/>
			)}
		</>
	);
}